
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { calculateNextServiceDate } from "@/lib/dateUtils";

export const useServiceLogs = (propertyId = null, applianceId = null) => {
    const { user } = useAuth();
    const [serviceLogs, setServiceLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchServiceLogs = useCallback(async () => {
        if (!user) return;

        let query = supabase
            .from("service_logs")
            .select("*")
            .order("date", { ascending: false });

        // Filter by property if provided
        if (propertyId) {
            query = query.eq("property_id", propertyId);
        }

        // Filter by appliance if provided
        if (applianceId) {
            query = query.eq("appliance_id", applianceId);
        }

        const { data, error } = await query;

        if (error) {
            toast({
                title: "Error",
                description: "Failed to fetch service logs",
                variant: "destructive",
            });
        } else {
            setServiceLogs(data || []);
        }
        setLoading(false);
    }, [user, propertyId, applianceId]);

    const addServiceLog = async (log) => {
        if (!user) return false;

        let nextServiceDate = log.nextServiceDate;

        // If it's an appliance service, try to get the appliance's frequency
        if (log.applianceId && !nextServiceDate && log.date) {
            const { data: appliance } = await supabase
                .from("appliances")
                .select("service_frequency, service_frequency_unit")
                .eq("id", log.applianceId)
                .single();

            if (appliance && appliance.service_frequency) {
                nextServiceDate = calculateNextServiceDate(
                    log.date,
                    appliance.service_frequency,
                    appliance.service_frequency_unit || "months"
                );
            }
        }

        // Fallback: Calculate 1 year follow-up if still no date and automated followup is checked
        if (log.automatedFollowup && !nextServiceDate && log.date) {
            nextServiceDate = calculateNextServiceDate(log.date, 1, 'years');
        }

        const { error } = await supabase.from("service_logs").insert({
            user_id: user.id,
            appliance_id: log.applianceId || null,
            provider_id: log.providerId || null,
            property_id: log.propertyId || propertyId || null,
            date: log.date || new Date().toISOString().split("T")[0],
            cost: log.cost || null,
            description: log.description || null,
            invoice_url: log.invoiceUrl || null,
            next_service_date: nextServiceDate || null,
            automated_followup: log.automatedFollowup || false,
        });

        if (error) {
            toast({
                title: "Error",
                description: "Failed to add service log",
                variant: "destructive",
            });
            return false;
        }

        // SYNC: Update the appliance's next_service_date if this log relates to an appliance
        if (log.applianceId && nextServiceDate) {
            const { error: applianceError } = await supabase
                .from("appliances")
                .update({
                    next_service_date: nextServiceDate,
                    last_service_date: log.date
                })
                .eq("id", log.applianceId);

            if (applianceError) console.error("Failed to sync appliance date:", applianceError);
        } else if (log.applianceId) {
            // Even if no next service date, still update the last service date
            await supabase
                .from("appliances")
                .update({
                    last_service_date: log.date
                })
                .eq("id", log.applianceId);
        }

        toast({
            title: "Service Logged",
            description: "Service has been logged successfully.",
        });

        fetchServiceLogs();
        return true;
    };

    const deleteServiceLog = async (id) => {
        // Fetch the log first to know which appliance it belongs to
        const { data: logToDelete } = await supabase
            .from("service_logs")
            .select("appliance_id")
            .eq("id", id)
            .single();

        const { error } = await supabase.from("service_logs").delete().eq("id", id);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to delete service log",
                variant: "destructive",
            });
            return false;
        }

        // SYNC: If it was an appliance log, recalculate the appliance's next_service_date
        if (logToDelete?.appliance_id) {
            // Get the most recent remaining log for this appliance
            const { data: nextLog } = await supabase
                .from("service_logs")
                .select("next_service_date, date")
                .eq("appliance_id", logToDelete.appliance_id)
                .order("date", { ascending: false })
                .limit(1)
                .maybeSingle();

            // Update appliance (revert or clear)
            await supabase
                .from("appliances")
                .update({
                    next_service_date: nextLog?.next_service_date || null,
                    last_service_date: nextLog?.date || null
                })
                .eq("id", logToDelete.appliance_id);
        }

        toast({
            title: "Service Log Deleted",
            description: "The service log has been removed.",
        });

        fetchServiceLogs();
        return true;
    };

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchServiceLogs();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchServiceLogs]);

    return {
        serviceLogs,
        loading,
        addServiceLog,
        deleteServiceLog,
        refetch: fetchServiceLogs,
    };
};
